import { HoverTip } from '../HoverTip';
import { type ReactNode, useState } from 'react';
import { colorFor, STATUS } from '../../lib/definitions/palette';
import { formatShortHexId } from '../../lib/format';
import { type NeighborTier } from '../PositionHistoryMap';


const VIA_RELAYS_TIP = "heard through repeaters; the hop was matched to this node by its unique ID prefix";

const BY_PATH_TIP = "heard through repeaters; the short ID matched several repeaters, and the rest of the path left only this one";


/** "direct 6 · relayed 3,541 · via relays 720", with a hover explanation on the "via relays" part. */
export function EvidenceSummary({ text }: { text: string }) {
  const parts = text.split(" · ");
  return (
    <>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && " · "}
          {p.startsWith("via relays") ? <HoverTip text={VIA_RELAYS_TIP}><span>{p}</span></HoverTip>
            : p.startsWith("Resolved By Path") ? <HoverTip text={BY_PATH_TIP}><span>{p}</span></HoverTip> : p}
        </span>
      ))}
    </>
  );
}


// battery_level is a PERCENT where a value above 100 means the node is
// powered/plugged in, not an over-100% battery (nodes.battery_level /
// telemetry.battery_level both use this convention) — render that as
// "Powered", never as e.g. "127%".
export function batteryLabel(level: number | null | undefined): string | null {
  if (level === null || level === undefined) return null;
  if (level > 100) return "Powered";
  return `${level}%`;
}


// --- Node identity tile grid (matches MeshMonitor's own node-detail card
// grid, per direct reference screenshots reviewed 2026-09-23 — one fact per
// rounded tile, small-caps muted label on top, larger value beneath, with
// semantic color only where a value has an unambiguous good/bad reading).
// No threshold for battery% existed anywhere in this codebase before now
// (grepped: only generic STATUS.good/warning/critical color tokens exist,
// no numeric cutoffs) — this build's own pick, chosen to reproduce the
// reference screenshot's own reading (21% battery renders critical there):
//   Battery:  <25% critical, 25-49% warning, >=50% good. "Powered" (>100,
//             see batteryLabel above) is not a percentage judgment at all
//             and stays neutral.
// Hops and Role are NOT colored — there's no unambiguous "good" hop count
// or role without a policy call this build doesn't have, and the
// reference itself leaves ROLE/HOPS AWAY neutral too.
//
// The single "Signal (SNR)" tile this grid used to carry (`node.snr` /
// `snrSemanticColor`) was removed 2026-09-26 (board item "Node page:
// MeshCore neighbours") — that value was always just the last SNR our
// radio measured on this node's last advert (usually the LAST RELAYING
// REPEATER's signal, not this node's own link quality), never cleared
// once stale, with thresholds invented for this build rather than sourced
// from any policy. Replaced by real per-link MeshCore neighbour evidence
// (meshcore_links, migration 0036) in the Neighbours section below, coloured
// by the stated SNR policy (audit/snr.py) instead.
type SemanticColor = "good" | "warning" | "critical" | undefined;


export function batterySemanticColor(level: number | null | undefined): SemanticColor {
  if (level === null || level === undefined || level > 100) return undefined;
  if (level < 25) return "critical";
  if (level < 50) return "warning";
  return "good";
}


// One fact, one tile — label/value/optional secondary line, hides itself
// (returns null) when value is genuinely absent, same contract as
// DetailField above just laid out as a card instead of a row.
export function InfoTile({
  label,
  value,
  secondary,
  color,
  title,
  wide,
  theme,
}: {
  label: string;
  value: ReactNode | null | undefined;
  secondary?: ReactNode;
  color?: SemanticColor;
  title?: string;
  wide?: boolean;
  theme: "light" | "dark";
}) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={wide ? "node-detail-tile node-detail-tile-wide" : "node-detail-tile"} title={title}>
      <div className="node-detail-tile-label">{label}</div>
      <div className="node-detail-tile-value" style={color ? { color: colorFor(STATUS[color], theme) } : undefined}>
        {value}
        {secondary && <span className="node-detail-tile-secondary"> {secondary}</span>}
      </div>
    </div>
  );
}


// Fix 7 (2026-09-26 operator review): the Node ID tile's raw value can be
// a 64-hex-character MeshCore native_id — plain .node-detail-tile-value
// (overflow-wrap: anywhere) wrapped that at whatever arbitrary column
// width the tile happened to have, producing an unreadable jagged
// mid-hex-digit break. Rather than inventing a new fixed-4-lines-of-16
// layout, this reuses the short-ID convention already established
// elsewhere in this exact codebase (lib/format.ts's formatShortHexId,
// used by BehaviourTab.tsx for the same "long id, short display" need)
// plus a copy button so the full value is still one click away — the
// full id remains visible on hover via the native `title` tooltip too.
export function NodeIdValue({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access denied — nothing to fall back to here; the id
      // is still readable (and selectable) via the title tooltip.
    }
  }

  return (
    <span className="node-detail-id-value" title={id}>
      <span className="node-detail-id-short">{formatShortHexId(id)}</span>
      <button type="button" className="node-detail-id-copy-btn" onClick={copy} title="Copy full node ID">
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}


// node_sightings.sighting_kind: 'reception' (heard on the air) vs
// 'roster_snapshot' (present in our radio's own node database as of this
// poll — NOT a live reception). Migration 0011 exists because conflating
// these caused a live incident; keep them visually distinct, not merged —
// both in the old text list and now on the position-history map and its
// summary list.
export const SIGHTING_KIND_LABEL: Record<string, string> = {
  reception: "Reception",
  roster_snapshot: "Roster snapshot",
};

export const SIGHTING_KIND_COLOR: Record<string, string> = {
  reception: "#0ca30c",
  roster_snapshot: "#c98500",
};

export const SIGHTING_KIND_TITLE: Record<string, string> = {
  reception: "Heard on the air at this time.",
  roster_snapshot: "Present in our radio's own node database as of this poll — not a live reception.",
};


// Same three tiers PositionHistoryMap draws (see neighborTier there) — used
// for the badges on the "neighbours without a position" list below the map,
// which can't be drawn as a line/marker but still deserves the same honest
// evidence-strength labeling.
export const NEIGHBOR_TIER_LABEL: Record<NeighborTier, string> = {
  bidirectional: "Bidirectional",
  "one-way": "One-way",
  "observed-only": "Observed only",
};

export const NEIGHBOR_TIER_COLOR: Record<NeighborTier, string> = {
  bidirectional: "#2a78d6",
  "one-way": "#c98500",
  "observed-only": "#898781",
};


// A plain "Xd"/"Xh"/"Xm"/"Xs" span, distinct from relativeTime's "X ago" —
// used only for the position-history summary's "N points over <span>" line,
// where the number is a duration, not an event's age.
export function formatDuration(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  return `${day}d`;
}


export function MetricCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="node-detail-metric-card">
      <div className="node-detail-metric-value">{value}</div>
      <div className="node-detail-metric-label">{label}</div>
    </div>
  );
}
